/* Mimi Shape: measurements storage + size logic (doc 3.2). No DOM here, so it is unit-testable
   (scripts/test-size.mjs) and shared by the product page, the finder, the cart and the complement. */
(() => {
  const Mimi = (window.Mimi = window.Mimi || {});
  const KEY = 'mimi:measurements';
  const TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 days (doc 3.2)

  /* Valid input ranges (doc 3.2). Under the lower bound = probably inches. */
  const LIMITS = { waist: [50, 160], hips: [70, 180] };

  const emit = () => {
    try { document.dispatchEvent(new CustomEvent('mimi:measurements', { detail: Mimi.measurements.get() })); } catch (e) { /* no DOM in tests */ }
  };

  Mimi.measurements = {
    get() {
      try {
        const raw = JSON.parse(localStorage.getItem(KEY));
        if (!raw || raw.v !== 1 || !raw.values) return null;
        if (Date.now() - Date.parse(raw.savedAt) > TTL_MS) { localStorage.removeItem(KEY); return null; }
        return raw.values;
      } catch (e) { return null; }
    },
    save(values) {
      try { localStorage.setItem(KEY, JSON.stringify({ v: 1, values, savedAt: new Date().toISOString() })); } catch (e) { /* storage blocked */ }
      emit();
    },
    clear() {
      try { localStorage.removeItem(KEY); } catch (e) { /* storage blocked */ }
      emit();
    },
  };

  /* 'ok' | 'inches' (below range) | 'high' (above range) | 'empty' */
  Mimi.validateMeasurement = (key, value) => {
    if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) return 'empty';
    const v = Number(value);
    const [min, max] = LIMITS[key] || [50, 180];
    if (v < min) return 'inches';
    if (v > max) return 'high';
    return 'ok';
  };

  /* Parse and sanity-check a size_chart metafield value. Returns null when unusable. */
  Mimi.parseChart = (chart) => {
    try {
      const c = typeof chart === 'string' ? JSON.parse(chart) : chart;
      if (!c || !Array.isArray(c.measurements) || !c.measurements.length || !Array.isArray(c.rows) || !c.rows.length) return null;
      const ok = c.rows.every((r) => r && r.size && c.measurements.every((m) => Array.isArray(r[m]) && r[m].length === 2 && r[m][0] <= r[m][1]));
      return ok ? c : null;
    } catch (e) { return null; }
  };

  /* Size for a set of measurements against one product's chart.
     Returns { status, size, sizes, rows } where status is:
       'one'        → size is the answer
       'between'    → sizes = [smaller, larger], no supplier rule to decide
       'outside'    → a measurement is outside the chart (write to us on WhatsApp)
       'incomplete' → a measurement the chart needs is missing
     rows: one entry per measurement { key, value, size, min, max } for the reasoning block. */
  Mimi.sizeFor = (chartInput, values) => {
    const chart = Mimi.parseChart(chartInput);
    if (!chart || !values) return { status: 'incomplete', rows: [] };
    const order = chart.rows.map((r) => r.size);
    const margin = Number(chart.boundary_cm) || 0;
    const rows = [];
    const candidates = new Set();

    for (const key of chart.measurements) {
      if (values[key] === undefined || values[key] === null || values[key] === '') return { status: 'incomplete', rows: [] };
      const v = Math.round(Number(values[key]));
      const idx = chart.rows.findIndex((r) => v >= r[key][0] && v <= r[key][1]);
      if (idx === -1) return { status: 'outside', rows: [{ key, value: v }] };
      const row = chart.rows[idx];
      rows.push({ key, value: v, size: row.size, min: row[key][0], max: row[key][1] });
      candidates.add(idx);
      // "On the boundary" (doc 3.2): within boundary_cm of an edge that has a neighbouring size
      if (margin > 0) {
        if (idx > 0 && v - row[key][0] < margin) candidates.add(idx - 1);
        if (idx < chart.rows.length - 1 && row[key][1] - v < margin) candidates.add(idx + 1);
      }
    }

    const sorted = [...candidates].sort((a, b) => a - b);
    if (sorted.length === 1) return { status: 'one', size: order[sorted[0]], rows };

    const rule = chart.between || {};
    if (rule.rule === 'larger') return { status: 'one', size: order[sorted[sorted.length - 1]], rows, ruled: true };
    if (rule.rule === 'smaller') return { status: 'one', size: order[sorted[0]], rows, ruled: true };
    if (rule.by) {
      const r = rows.find((x) => x.key === rule.by);
      if (r) return { status: 'one', size: r.size, rows, ruled: true };
    }
    return { status: 'between', sizes: [order[sorted[0]], order[sorted[sorted.length - 1]]], rows };
  };

  /* One size to recommend (copy v7: no "between" text, the finder follows the supplier's rule).
     The chart's own rule wins (sizeFor), else the theme setting Mimi.betweenRule ('larger' | 'smaller').
     Returns the size, or null when a measurement is missing or outside the chart. */
  Mimi.recommendSize = (chartInput, values) => {
    const r = Mimi.sizeFor(chartInput, values);
    if (r.status === 'one') return r.size;
    if (r.status === 'between') return Mimi.betweenRule === 'smaller' ? r.sizes[0] : r.sizes[r.sizes.length - 1];
    return null;
  };

  Mimi.SIZE_LIMITS = LIMITS;
})();
